import { useState } from "react";
import { EmailInviteContextDto, OrgWithOwnerDto, bffApi } from "../../../../autogen/bff-api";
import { useLoggedOutState } from "../../../../common/auth/useLoggedOutState";
import { useApiError } from "../../../../common/errors/useApiError";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { signupAsOrgThunk } from "../../../../common/redux/thunks/auth/signup-as-org-thunk";
import { useBaseData } from "../../../../common/useBaseData";
import { DuplicateOrgsModal } from "../../../auth/shared/DuplicateOrgsModal";
import { SignupAsOrgForm, SignupAsOrgFormValues } from "../../../auth/signup/SignupAsOrgForm";

interface Props {
  invitationContext: EmailInviteContextDto | null;
}

export const SignupAsInvitedOrg = ({ invitationContext }: Props) => {
  const dispatch = useAppDispatch();
  const authState = useLoggedOutState();
  const baseData = useBaseData();
  const apiError = useApiError();

  const [duplicateOrgs, setDuplicateOrgs] = useState<OrgWithOwnerDto[] | null>(null);
  const [pendingValues, setPendingValues] = useState<SignupAsOrgFormValues | null>(null);
  const [isProbing, setIsProbing] = useState(false);

  const dispatchSignup = (values: SignupAsOrgFormValues) => {
    const trimmedOrgnr = values.organizationNumber?.replace(/\s+/g, "");
    dispatch(
      signupAsOrgThunk({
        loginCredentials: { email: values.email, password: values.password },
        personCredentials: { firstName: values.firstName, lastName: values.lastName },
        orgCredentials: {
          companyName: values.companyName,
          countryId: values.countryId,
          organizationNumber: trimmedOrgnr,
        },
        invitationContext,
      })
    );
  };

  const onSubmit = async (values: SignupAsOrgFormValues) => {
    setIsProbing(true);
    try {
      const country = baseData.countries.find((c) => c.id === values.countryId);
      const trimmedOrgnr = values.organizationNumber?.replace(/\s+/g, "");
      if (country && trimmedOrgnr) {
        try {
          const { orgs } = await dispatch(
            bffApi.endpoints.listOrgsByNumber.initiate({ country: country.alpha3Code, number: trimmedOrgnr })
          ).unwrap();
          if (orgs.length > 0) {
            setPendingValues(values);
            setDuplicateOrgs(orgs);
            return;
          }
        } catch (e) {
          apiError.trigger(e);
          return;
        }
      }
      dispatchSignup(values);
    } finally {
      setIsProbing(false);
    }
  };

  const onCancel = () => {
    setDuplicateOrgs(null);
    setPendingValues(null);
  };

  const onContinue = () => {
    if (pendingValues) dispatchSignup(pendingValues);
    setDuplicateOrgs(null);
    setPendingValues(null);
  };

  return (
    <>
      <DuplicateOrgsModal duplicateOrgs={duplicateOrgs} onCancel={onCancel} onContinue={onContinue} />
      <SignupAsOrgForm
        isSubmitting={isProbing || authState.isSigningUp}
        signupError={authState.signupError}
        onSubmit={onSubmit}
      />
    </>
  );
};
